'use client';

import { useTranslation } from 'next-i18next';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { useBestieChatStateOptional } from '@components/contexts/BestieChatStateContext';

/**
 * Loads the thread's past messages into the chat, on demand.
 *
 * Entering the chat page shows Bestie's greeting only; this is how the visitor asks for
 * what came before. It is a one-shot control — once history is merged in there is nothing left
 * to do, so the button unmounts. A failed load leaves it in place so the click can be retried.
 *
 * Renders nothing outside `BestieChatStateProvider`, which keeps it inert for the other
 * `ChatBranch` callers (stories answer bar, interview answer bar, job-list search bar).
 */
export const ChatHistoryButton = () => {
  const { t } = useTranslation('common');
  const chatState = useBestieChatStateOptional();

  if (!chatState || chatState.hasHistoryLoaded) return null;

  const { loadHistory, isHistoryLoading } = chatState;

  return (
    <Button
      type="button"
      styling="outline"
      color="light"
      rounding="none"
      isLoading={isHistoryLoading}
      aria-label={t('global.label.history', 'History')}
      className="h-9! w-9! shrink-0 rounded-lg! border-none! bg-surface-100 p-0! transition-transform hover:scale-110 lg:h-10! lg:w-10!"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { void loadHistory(); }}
    >
      {isHistoryLoading ? null : <ClockIcon className="size-5 text-ink" />}
    </Button>
  );
};
